import * as React from 'react'
import { Link } from '@inertiajs/react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'

/**
 * One page language, used everywhere.
 *
 * Before this existed each page invented its own: eight different section
 * headings, eight different list containers, four different page titles. The
 * app read like four products stitched together. Nothing here is clever — the
 * value is that every page uses the same five things.
 */

/** Vertical rhythm for a page. Sections are evenly spaced; nothing sets its own margins. */
export function Page({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('space-y-10', className)}>{children}</div>
}

/** The one page title. Badge sits inline with it; actions sit under the subtitle. */
export function PageHeader({
  title,
  badge,
  subtitle,
  actions,
  children,
}: {
  title: React.ReactNode
  badge?: React.ReactNode
  subtitle?: React.ReactNode
  actions?: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    <header className="space-y-3">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">{title}</h1>
          {badge}
        </div>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      {children}
    </header>
  )
}

/** The one section heading: a quiet uppercase eyebrow, with room for one action. */
export function Section({
  title,
  action,
  headingId,
  children,
  className,
}: {
  title?: React.ReactNode
  action?: React.ReactNode
  headingId?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <section aria-labelledby={headingId} className={cn('space-y-3', className)}>
      {title && (
        <div className="flex items-center justify-between gap-3">
          <h2 id={headingId} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {title}
          </h2>
          {action}
        </div>
      )}
      {children}
    </section>
  )
}

/** The one list container. */
export function Rows({
  children,
  columns,
  className,
}: {
  children: React.ReactNode
  columns?: boolean
  className?: string
}) {
  return (
    <ItemGroup className={cn(columns ? 'grid gap-3 sm:grid-cols-2' : 'gap-2', className)}>{children}</ItemGroup>
  )
}

/**
 * The one row. A row that links shows a chevron and nothing else clickable —
 * a row with its own button is not itself a link, so it never gets one.
 */
export function Row({
  icon,
  title,
  description,
  meta,
  href,
  action,
  muted,
  className,
}: {
  icon?: React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  meta?: React.ReactNode
  href?: string
  action?: React.ReactNode
  muted?: boolean
  className?: string
}) {
  const body = (
    <>
      {icon && <ItemMedia variant="icon">{icon}</ItemMedia>}
      <ItemContent>
        <ItemTitle>{title}</ItemTitle>
        {description && <ItemDescription>{description}</ItemDescription>}
      </ItemContent>
      {(meta || action || href) && (
        <ItemActions>
          {meta}
          {action}
          {href && <ChevronRight className="size-4 text-muted-foreground" />}
        </ItemActions>
      )}
    </>
  )

  if (href) {
    return (
      <Item variant="outline" asChild className={cn(muted && 'border-dashed bg-muted/30', className)}>
        <Link href={href}>{body}</Link>
      </Item>
    )
  }
  return (
    <Item variant="outline" className={cn(muted && 'border-dashed bg-muted/30', className)}>
      {body}
    </Item>
  )
}

/** The one empty state. Everything but the message is optional. */
export function Empty({
  icon,
  title,
  children,
  action,
}: {
  icon?: React.ReactNode
  title?: React.ReactNode
  children?: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed px-6 py-10 text-center">
      {icon && <div className="mb-3 text-muted-foreground/40">{icon}</div>}
      {title && <h3 className="text-sm font-medium text-foreground">{title}</h3>}
      {children && <p className={cn('text-sm text-muted-foreground', title && 'mt-1')}>{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

/** The one key/value block, for the record details every service page shows. */
export function Facts({ items }: { items: Array<{ label: string; value: React.ReactNode }> }) {
  return (
    <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
      {items.map((f) => (
        <div key={f.label} className="space-y-1">
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{f.label}</dt>
          <dd className="text-sm text-foreground">{f.value}</dd>
        </div>
      ))}
    </dl>
  )
}
